/**
 * Steering and throttle checks for a rover or a boat, on the bench.
 *
 * A copter's motor test does not fit a boat: ArduRover numbers its test
 * outputs by role, so "motor 2" is the rudder, and the RC override a plane's
 * surface check uses cannot move anything on a disarmed Rover. This drives
 * ArduRover's own motor test by output name instead. The vehicle stays
 * disarmed (the autopilot arms itself for the test and disarms after) and no
 * setting on the flight controller is written.
 *
 * Every check asks once before it is sent, like the motor test. The bounds
 * mirror `api/app/domain/surface_vehicle.py`; the server checks them again.
 */

import { useState, type ReactNode } from 'react';

import { droneCommands, type DriveOutput } from '@/api/drones';
import { useCanControl } from '@/stores/authStore';
import { useDrone } from '@/stores/droneStore';
import type { CommandResult, DroneId } from '@/types/domain';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';

/** Mirrors MAX_STEERING_PERCENT, MAX_DRIVE_THROTTLE_PERCENT and MAX_DURATION_S. */
const MAX_STEERING = 100;
const MAX_THROTTLE = 30;
const MAX_DURATION = 10;

interface Check {
  output: DriveOutput;
  percent: number;
  label: string;
}

export function DriveTestPanel({ droneId }: { droneId: DroneId }): ReactNode {
  const canCommand = useCanControl(droneId);
  const drone = useDrone(droneId);
  const [steering, setSteering] = useState(50);
  const [throttle, setThrottle] = useState(10);
  const [duration, setDuration] = useState(2);
  const [pending, setPending] = useState<Check | null>(null);
  const [result, setResult] = useState<CommandResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const armed = drone?.scalars.armed === true;
  const disabled = !canCommand || busy || armed;

  async function send(check: Check) {
    setBusy(true);
    setError(null);
    setResult(null);
    const response = await droneCommands.driveTest(droneId, check.output, check.percent, duration);
    setBusy(false);
    setPending(null);
    if (response.ok) setResult(response.data);
    else setError(response.error.message);
  }

  const ask = (check: Check) => {
    setResult(null);
    setError(null);
    setPending(check);
  };

  const button = (check: Check) => (
    <button
      key={check.label}
      type="button"
      disabled={disabled}
      onClick={() => ask(check)}
      className="rounded-[10px] border px-3 py-1.5 label-mono text-[10px] disabled:opacity-40"
      style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(244,244,245,0.85)' }}
    >
      {check.label}
    </button>
  );

  return (
    <div className="flex flex-col gap-3">
      <div
        className="rounded-[12px] border px-3 py-2.5 text-[11px]"
        style={{
          background: 'color-mix(in oklch, oklch(0.72 0.19 28) 9%, transparent)',
          borderColor: 'color-mix(in oklch, oklch(0.72 0.19 28) 38%, transparent)',
        }}
      >
        <span className="label-mono text-[9px]" style={{ color: DANGER, letterSpacing: '0.14em' }}>
          SECURE THE VEHICLE
        </span>
        <p className="mt-1" style={{ color: 'rgba(244,244,245,0.85)' }}>
          These move the rudder and turn the propeller. Keep hands clear of both and make sure the
          vehicle cannot drive off. It stays disarmed: the autopilot arms itself for the test and
          disarms after, and no settings are changed.
        </p>
      </div>

      {!canCommand && (
        <p className="text-[11px]" style={{ color: MUTED }}>
          Read-only: a drive test needs tactical role and control of this vehicle.
        </p>
      )}
      {armed && (
        <p className="text-[11px]" style={{ color: WARN }}>
          This vehicle is armed. Disarm it before testing its steering or throttle.
        </p>
      )}

      <Section title="STEERING" hint={`${steering}% each way`}>
        <input
          type="range"
          min={10}
          max={MAX_STEERING}
          step={10}
          value={steering}
          aria-label="Steering percent"
          onChange={(e) => {
            setSteering(Number(e.target.value));
            setPending(null);
          }}
          className="w-full"
        />
        <div className="flex gap-2">
          {button({ output: 'steering', percent: -steering, label: 'LEFT' })}
          {button({ output: 'steering', percent: steering, label: 'RIGHT' })}
        </div>
      </Section>

      <Section title="THROTTLE" hint={`${throttle}%, max ${MAX_THROTTLE}%`}>
        <input
          type="range"
          min={1}
          max={MAX_THROTTLE}
          step={1}
          value={throttle}
          aria-label="Throttle percent"
          onChange={(e) => {
            setThrottle(Number(e.target.value));
            setPending(null);
          }}
          className="w-full"
        />
        <div className="flex flex-wrap gap-2">
          {button({ output: 'throttle', percent: throttle, label: 'FORWARD' })}
          {button({ output: 'throttle', percent: -throttle, label: 'REVERSE' })}
          {/* Skid-steer hulls have a throttle per side and no rudder. The
              autopilot refuses whichever of these this vehicle has no output
              for, and says which setting to look at. */}
          {button({ output: 'throttle_left', percent: throttle, label: 'LEFT MOTOR' })}
          {button({ output: 'throttle_right', percent: throttle, label: 'RIGHT MOTOR' })}
        </div>
      </Section>

      <Section title={`DURATION ${duration}s`} hint={`max ${MAX_DURATION}s`}>
        <input
          type="range"
          min={1}
          max={MAX_DURATION}
          step={1}
          value={duration}
          aria-label="Duration seconds"
          onChange={(e) => {
            setDuration(Number(e.target.value));
            setPending(null);
          }}
          className="w-full"
        />
      </Section>

      {pending && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void send(pending)}
            className="rounded-[10px] border px-4 py-2 label-mono text-[11px] disabled:opacity-40"
            style={{
              borderColor: 'color-mix(in oklch, oklch(0.72 0.19 28) 55%, transparent)',
              background: 'color-mix(in oklch, oklch(0.72 0.19 28) 18%, transparent)',
              color: DANGER,
            }}
          >
            {busy
              ? 'SENDING…'
              : `CONFIRM: ${pending.label} AT ${Math.abs(pending.percent)}% FOR ${duration}s`}
          </button>
          <button
            type="button"
            onClick={() => setPending(null)}
            className="rounded-[10px] border px-3 py-2 label-mono text-[10px]"
            style={{ borderColor: 'rgba(255,255,255,0.18)', color: MUTED }}
          >
            BACK
          </button>
        </div>
      )}

      {result && (
        <p className="text-[11px]" style={{ color: result.ok ? OK : DANGER }}>
          {result.message || (result.ok ? 'Accepted.' : 'Refused.')}
        </p>
      )}
      {error && (
        <p className="text-[11px]" style={{ color: DANGER }}>
          {error}
        </p>
      )}
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-baseline gap-2">
        <span className="label-mono text-[10px]" style={{ color: 'rgba(244,244,245,0.8)' }}>
          {title}
        </span>
        <span className="label-mono text-[9px]" style={{ color: MUTED }}>
          {hint}
        </span>
      </span>
      {children}
    </div>
  );
}
